// import dateFormat from 'dateformat';
import Api from "./Api";
import AuthManager from "./AuthManager";
import { PubEvent, PubEventMedia } from "./data/Responses";

export enum EventListMode {
    events = "events",
    myEvents = "myEvents",
    pastEvents = "pastEvents",
    memories = "memories",
}

export const enum DateHeader {
    now = "Happening Now",
    today = "Today",
    week = "This Week",
    nextWeek = "Next Week",
    thisMonth = "This Month",
    nextMonth = "Next Month",
    upcoming = "Upcoming",
}

type EventGroup = { header: DateHeader } | { event: PubEvent };

export default class EventListViewModel {
    private _mode: EventListMode = EventListMode.events;
    private allEvents: PubEvent[] = [];
    isLoaded = false;
    private _canLoadMore = true;
    displayedEvents: any[] = [];
    memoriesOffset = 0;
    memoriesGroups: { eventName: string; media: PubEventMedia[] }[] = [];
    isLoadingMore = false;

    constructor(
        private api: Api,
        private authManager: AuthManager,
    ) {}

    public get canLoadMore(): boolean {
        if (this._mode === EventListMode.memories) {
            return this._canLoadMore;
        }
        if (this._mode === EventListMode.pastEvents) {
            return this.canLoadBackward;
        }
        return this.canLoadForward;
    }

    private set canLoadMore(value: boolean) {
        this._canLoadMore = value;
    }

    public get mode(): EventListMode {
        return this._mode;
    }

    public set mode(m: EventListMode) {
        const oldMode = this._mode;
        this._mode = m;

        // If switching to or from memories mode, we need a full reload
        if (oldMode === EventListMode.memories || m === EventListMode.memories) {
            this.initialLoad();
            return;
        }

        // For other modes, just update the displayed events from cache
        switch (m) {
            case EventListMode.events:
                this.displayedEvents = this.groupedEvents;
                break;
            case EventListMode.myEvents:
                this.displayedEvents = this.myEvents;
                break;
            case EventListMode.pastEvents:
                this.displayedEvents = this.pastEvents;
                // If we haven't loaded any past events yet, load them
                if (this.pastEvents.length === 0 && this.canLoadBackward) {
                    this.loadMore();
                }
                break;
        }
    }

    canLoadForward = true;
    canLoadBackward = true;
    maxLoad = 25;

    groupedEvents: Array<EventGroup> = [];
    myEvents: Array<EventGroup> = [];
    pastEvents: Array<EventGroup> = [];

    async initialLoad() {
        this.isLoaded = false;
        this._canLoadMore = true;
        this.canLoadForward = true;
        this.canLoadBackward = true;
        this.isLoadingMore = false;
        this.displayedEvents = [];
        this.memoriesGroups = [];
        this.memoriesOffset = 0;
        this.allEvents = [];

        await this.loadMore();
    }

    async loadMore() {
        if (this.isLoadingMore) {
            return;
        }
        this.isLoadingMore = true;

        try {
            if (this.mode === EventListMode.memories) {
                await this.loadMoreMemories();
            } else if (this._mode === EventListMode.pastEvents) {
                await this.loadBackward();
            } else {
                await this.loadForward();
            }
        } finally {
            this.isLoadingMore = false;
        }
    }

    async loadForward() {
        if (!this.canLoadForward) {
            return;
        }

        var startTime = Date.now();
        const first = this.allEvents[0];
        if (first) {
            startTime = first.time;
        }
        const newEvents = await this.api.getEvents({ limit: this.maxLoad, after: startTime, forward: true });
        this.canLoadForward = newEvents.length > 0 && newEvents.length == this.maxLoad;
        this.allEvents = newEvents.reverse().concat(this.allEvents);

        await this.reloadView();
    }

    async loadBackward() {
        if (!this.canLoadBackward) {
            return;
        }

        var startTime = Date.now();
        const last = this.allEvents[this.allEvents.length - 1];
        if (last) {
            startTime = last.time;
        }
        const newEvents = await this.api.getEvents({ limit: 25, after: startTime, forward: false });
        this.canLoadBackward = newEvents.length > 0 && newEvents.length == this.maxLoad;
        this.allEvents = this.allEvents.concat(newEvents);

        await this.reloadView();
    }

    async reloadView() {
        if (this._mode === EventListMode.memories) {
            // For memories mode, we don't need to do anything here
            // as loadMoreMemories handles setting isLoaded
            return;
        }

        const now = new Date();
        const midnight = new Date();
        midnight.setHours(0, 0, 0, 0);

        const myEvents = this.allEvents
            .filter((event) => event.owner.id == this.authManager.metadata?.user.id)
            .sort((a, b) => a.time - b.time)
            .filter((event) => new Date(event.time) >= midnight);

        this.myEvents = this.appendHeaders(myEvents, false);

        const events = this.allEvents.filter((event) => new Date(event.time) >= midnight && (event.endTime == null || new Date(event.endTime) > now)).sort((a, b) => a.time - b.time);
        this.groupedEvents = this.appendHeaders(events, true);

        const pastEvents = this.allEvents.filter((event) => new Date(event.time) < now).sort((a, b) => b.time - a.time);
        this.pastEvents = this.appendHeaders(pastEvents, false);

        switch (this._mode) {
            case EventListMode.events:
                this.displayedEvents = this.groupedEvents;
                break;
            case EventListMode.myEvents:
                this.displayedEvents = this.myEvents;
                break;
            case EventListMode.pastEvents:
                this.displayedEvents = this.pastEvents;
                break;
        }

        this.isLoaded = true;
    }

    appendHeaders(events: PubEvent[], appendEnabled: boolean): EventGroup[] {
        // Create an array to hold the grouped events
        const groupedEvents: EventGroup[] = [];

        // Group the events by date header
        let currentHeader: DateHeader | null = null;

        events.forEach((event) => {
            const eventHeader = this.getEventHeader(event.time, event.endTime);

            if (eventHeader !== currentHeader && appendEnabled) {
                currentHeader = eventHeader;
                groupedEvents.push({ header: eventHeader });
            }

            groupedEvents.push({ event });
        });

        return groupedEvents;
    }

    getEventHeader(startTime: number, endDate?: number): DateHeader {
        const eventDate = new Date(startTime);
        const endTime = endDate ? new Date(endDate) : null;

        if (this.isDateNow(eventDate, endTime)) {
            return DateHeader.now;
        } else if (this.isDateToday(eventDate)) {
            return DateHeader.today;
        } else if (this.isWithinCurrentWeek(eventDate)) {
            return DateHeader.week;
        } else if (this.isNextWeek(eventDate)) {
            return DateHeader.nextWeek;
        } else if (this.isThisMonth(eventDate)) {
            return DateHeader.thisMonth;
        } else if (this.isNextMonth(eventDate)) {
            return DateHeader.nextMonth;
        } else {
            return DateHeader.upcoming;
        }
    }

    isDateNow(date: Date, endDate: Date | null): boolean {
        const now = new Date();

        // get the start and end times in milliseconds
        const startTime = date.getTime();
        const endTime = endDate ? endDate.getTime() : Infinity;

        // check if the current time is between the start and end times
        return now.getTime() >= startTime && now.getTime() < endTime;
    }

    isDateToday(date: Date): boolean {
        const now = new Date();
        return date.getFullYear() == now.getFullYear() && date.getMonth() == now.getMonth() && date.getDate() == now.getDate();
    }

    isWithinCurrentWeek(date: Date): boolean {
        const now = new Date();
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDate());
        const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDate() + 6);
        return date >= startOfWeek && date <= endOfWeek;
    }

    isNextWeek(date: Date): boolean {
        const now = new Date();
        const startOfNextWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDate() + 7);
        const endOfNextWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDate() + 13);
        return date >= startOfNextWeek && date <= endOfNextWeek;
    }

    isThisMonth(date: Date): boolean {
        const now = new Date();
        return date.getFullYear() == now.getFullYear() && date.getMonth() == now.getMonth();
    }

    isNextMonth(date: Date): boolean {
        const now = new Date();
        return date.getFullYear() == now.getFullYear() && date.getMonth() == now.getMonth() + 1;
    }

    private async loadMoreMemories() {
        try {
            const photos = await this.api.getAllPhotos({
                offset: this.memoriesOffset,
                limit: 30,
            });

            if (photos.length === 0) {
                this._canLoadMore = false;
                this.isLoaded = true;
                return;
            }

            // Group photos by event
            const newGroups: { [key: string]: PubEventMedia[] } = {};
            photos.forEach((photo) => {
                if (!photo.eventName) return;
                if (!newGroups[photo.eventName]) {
                    newGroups[photo.eventName] = [];
                }
                newGroups[photo.eventName].push(photo);
            });

            // Convert to array format and append to existing groups
            Object.entries(newGroups).forEach(([eventName, media]) => {
                this.memoriesGroups.push({ eventName, media });
            });

            this.memoriesOffset += photos.length;
            this._canLoadMore = photos.length === 30;
            this.isLoaded = true;
        } catch (error) {
            console.error("Error loading photos:", error);
            this._canLoadMore = false;
            this.isLoaded = true;
        }
    }
}
